import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiGet, apiPath, apiPost } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type { NimAnalysisDetail, TriggerNimRequest } from './types';

const nimKey = (date: string | undefined) =>
  ['alm', 'nim', 'latest', date] as const;

/**
 * GET /api/v1/alm/nim?date=YYYY-MM-DD
 *
 * Returns the persisted NIM result for the given tenant + date, or null
 * if no calculation has run for that date yet.
 */
export function useLatestNim(
  date: string | undefined,
  options?: Omit<
    UseQueryOptions<NimAnalysisDetail | null, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: nimKey(date),
    enabled: Boolean(date),
    queryFn: async () => {
      const url = `${apiPath('alm', 'nim')}?date=${encodeURIComponent(date!)}`;
      const data = await apiGet<NimAnalysisDetail | null>(url);
      return data ?? null;
    },
    ...options,
  });
}

/**
 * POST /api/v1/alm/nim/calculate — triggers an on-demand NIM calculation.
 * Server returns 202 Accepted with the NimAnalysisDetail.
 */
export function useTriggerNim(
  options?: UseMutationOptions<NimAnalysisDetail, CubixApiError, TriggerNimRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPost<NimAnalysisDetail, TriggerNimRequest>(
        apiPath('alm', 'nim', 'calculate'),
        body,
      ),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['alm', 'nim'] });
      return options?.onSuccess?.(...args);
    },
  });
}
